import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import { memo, useState } from 'react'
import { toast } from 'react-toastify'

import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { useDeleteMentor } from '../../api/apiHooks'
import { IMentor } from '../../api/types'
import { Modal } from '../Modal/Modal'

interface ITreeDotsDropdownProps {
  mentor: IMentor
  onEditMenuClick?: () => void
}

const TreeDotsDropdown: React.FC<ITreeDotsDropdownProps> = memo(
  ({ mentor, onEditMenuClick }) => {
    const { mutateAsync, isPending } = useDeleteMentor()
    const [isOpen, setIsOpen] = useState(false)
    const queryClient = useQueryClient()

    const deleteMentor = async (): Promise<void> => {
      try {
        const res = await mutateAsync(mentor.mentorId)
        if (res.success) {
          setIsOpen(false)
          void queryClient.invalidateQueries({ queryKey: ['get-all-mentors'] })
          // Invalidate non-admin queries to sync data
          void queryClient.invalidateQueries({
            queryKey: ['get-associated-mentor'],
          })
          void queryClient.invalidateQueries({
            queryKey: ['get-featured-mentors'],
          })
          toast.success(
            `Companion ${mentor.firstName} ${mentor.lastName} deleted successfully`,
          )
        }
      } catch (error) {
        console.error('Error removing companion:', error)
        toast.error(
          `Failed to delete companion ${mentor.firstName} ${mentor.lastName}`,
        )
      }
    }

    const handleClose = () => {
      setIsOpen(false)
    }

    const handleOpen = () => {
      setIsOpen(true)
    }

    return (
      <>
        <Menu>
          <MenuButton className="p-1 cursor-pointer text-gray-400 hover:text-white block leading-none">
            <i className="fa-solid fa-ellipsis-vertical" />
          </MenuButton>
          <MenuItems
            modal={false}
            anchor="bottom end"
            className="w-40 shadow-lg z-50 tc-mv-blur rounded-regular p-4"
            transition
          >
            <MenuItem
              as="button"
              onClick={onEditMenuClick}
              className="w-full px-1 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center gap-2 transition-colors duration-150"
            >
              Edit companion
            </MenuItem>
            <MenuItem
              as="button"
              onClick={handleOpen}
              className="w-full px-1 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center gap-2 transition-colors duration-150"
            >
              Delete companion
            </MenuItem>
          </MenuItems>
        </Menu>
        {isOpen && (
          <Modal title="Delete companion" onClose={handleClose}>
            <>
              <p className="text-white py-4 px-6">
                Are you sure you want to delete{' '}
                {`${mentor.firstName} ${mentor.lastName}`}? This action cannot
                be undone.
              </p>
              <div className="flex justify-end gap-4 border-t border-white/10 py-4 px-6">
                <button
                  onClick={handleClose}
                  className="btn btn-secondary hover:bg-red-200 transition-bg"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteMentor}
                  disabled={isPending}
                  className="btn bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {isPending && <AnimatedSpinner className="mr-2" />}
                  Delete
                </button>
              </div>
            </>
          </Modal>
        )}
      </>
    )
  },
)

export { TreeDotsDropdown }
